import { ChangeDetectionStrategy, Component, Input, OnInit, OnDestroy } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule, provideNativeDateAdapter } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, BehaviorSubject, takeUntil } from 'rxjs';
import { ActivityFormService } from '../activity-form/activity-form.service';
import * as dayjs from 'dayjs';

@Component({
	selector: 'ngx-datetime-picker',
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatDatepickerModule,
		MatNativeDateModule,
		MatIconModule,
		MatButtonModule,
		TranslateModule
	],
	templateUrl: './ngx-datetime-picker.component.html',
	styleUrls: ['./ngx-datetime-picker.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [provideNativeDateAdapter()]
})
export class NgxDatetimePickerComponent implements OnInit, OnDestroy {
	@Input() public property: string = '';
	public minDate: Date | null = null;
	public maxDate: Date | null = null;
	public timeString$ = new BehaviorSubject<string>('');

	private readonly destroy$ = new Subject<void>();

	constructor (
		private activityFormService: ActivityFormService,
		private translate: TranslateService
	) {}

	public get control (): FormControl | null {
		return this.activityFormService.getControl(this.property);
	}

	public get label (): string {
		const labelMap: { [key: string]: string } = {
			available_at: 'activity-editor-new_availability-date-picker-label-no-format',
			due_at: 'activity-editor-new_due-date-picker-label-no-format'
		};

		return this.translate.instant(labelMap[this.property]);
	}

	public ngOnInit (): void {
		if (this.control?.value) {
			const dateValue = this.parseDate(this.control.value);
			if (dateValue) {
				const hours = dateValue.getHours().toString().padStart(2, '0');
				const minutes = dateValue.getMinutes().toString().padStart(2, '0');
				this.timeString$.next(`${hours}:${minutes}`);
			}
		}
		this.initializeMinMaxDates();
		this.watchMinMaxDates();
	}

	public onDatePickerClosed (): void {
		if (!this.timeString$.value && this.control?.value) {
			this.timeString$.next('00:00');
		}
		this.updateControlValue();
	}

	public ngOnDestroy (): void {
		this.destroy$.next();
		this.destroy$.complete();
		this.timeString$.complete();
	}

	public onTimeChange (event: Event): void {
		const input = event.target as HTMLInputElement;
		this.timeString$.next(input.value);
		this.updateControlValue();
	}

	public clearValue (): void {
		this.timeString$.next('');
		if (this.control) {
			this.control.setValue(null);
			this.control.markAsDirty();
		}
	}

	private initializeMinMaxDates (): void {
		if (this.property === 'available_at') {
			const dueDate = this.activityFormService.getControl('due_at').value;
			if (dueDate) {
				const dateValue = this.parseDate(dueDate);
				if (dateValue) {
					this.maxDate = dateValue;
				}
			}
		} else if (this.property === 'due_at') {
			const availableDate = this.activityFormService.getControl('available_at').value;
			if (availableDate) {
				const dateValue = this.parseDate(availableDate);
				if (dateValue) {
					this.minDate = dateValue;
				}
			}
		}
	}

	private watchMinMaxDates (): void {
		const otherProperty = this.property === 'available_at' ? 'due_at' : 'available_at';
		const otherControl = this.activityFormService.getControl(otherProperty);

		if (otherControl) {
			otherControl.valueChanges
				.pipe(takeUntil(this.destroy$))
				.subscribe((value) => {
					const dateValue = this.parseDate(value);

					if (this.property === 'available_at') {
						// available_at should not be after due_at
						this.maxDate = dateValue;
					} else if (this.property === 'due_at') {
						// due_at should not be before available_at
						this.minDate = dateValue;
					}
				});
		}
	}

	private updateControlValue (): void {
		const combinedValue = this.getCombinedValue();
		if (this.control) {
			const currentValue = this.control.value;
			if (!dayjs(currentValue).isSame(dayjs(combinedValue))) {
				this.control.setValue(combinedValue);
				this.control.markAsDirty();
			}
		}
	}

	private getCombinedValue (): Date | null {
		const dateValue = this.control?.value;
		const parsedDate = this.parseDate(dateValue);

		if (!parsedDate) {
			return null;
		}

		const result = new Date(parsedDate);

		if (this.timeString$.value) {
			const [hours, minutes] = this.timeString$.value.split(':').map(Number);
			result.setHours(hours || 0);
			result.setMinutes(minutes || 0);
			result.setSeconds(0);
			result.setMilliseconds(0);
		} else {
			result.setHours(0, 0, 0, 0);
		}

		return result;
	}

	private parseDate (value: any): Date | null {
		if (!value) {
			return null;
		}

		if (value instanceof Date) {
			return value;
		}

		if (typeof value === 'string') {
			const parsed = new Date(value);
			return isNaN(parsed.getTime()) ? null : parsed;
		}

		return null;
	}
}
